import React, { useEffect, useState } from "react";
import { Save, Share2, ExternalLink, RefreshCw, Loader2, Image as ImageIcon, Trash2, ChevronDown, ChevronRight, Search } from "lucide-react";
import { toast } from "sonner";
import { adminApi, publicApi, API } from "../../lib/api";
import ImageUpload from "./ImageUpload";
import ShareReport from "./ShareReport";

const inputCls =
  "w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40 focus:border-orange";

const MODE_HINT = {
  generated: "A Cassie card with the page's own headline on it.",
  own: "The photograph the page already has.",
  off: "No card — the link unfurls with the site default.",
};

export default function ShareCardsManager() {
  const [data, setData] = useState(null);
  const [modes, setModes] = useState({});
  const [overrides, setOverrides] = useState({});
  const [saving, setSaving] = useState(false);
  const [path, setPath] = useState("/ask-cassie");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [tab, setTab] = useState("settings");
  const [overridePath, setOverridePath] = useState("");
  const [overrideImage, setOverrideImage] = useState("");
  const [pages, setPages] = useState(null);
  const [loadingPages, setLoadingPages] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const [filters, setFilters] = useState({});

  const load = () =>
    adminApi.shareCards.get().then((d) => {
      setData(d);
      setModes(Object.fromEntries(d.sections.map((s) => [s.key, s.mode])));
      setOverrides(d.overrides || {});
    });

  useEffect(() => {
    load().catch(() => toast.error("Couldn't load the share card settings"));
  }, []);

  const check = (p) => {
    setLoadingPreview(true);
    publicApi
      .shareMeta(p)
      .then(setPreview)
      .catch(() => toast.error("Couldn't preview that URL"))
      .finally(() => setLoadingPreview(false));
  };

  useEffect(() => {
    if (data) check("/ask-cassie");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!data]);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.shareCards.update({ sections: modes, overrides });
      toast.success("Share cards saved");
      await load();
      check(path);
    } catch {
      toast.error("Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (key) => {
    setOpenSection(openSection === key ? null : key);
    if (!pages && !loadingPages) {
      setLoadingPages(true);
      adminApi.shareCards
        .pages()
        .then((d) => setPages(d.sections || {}))
        .catch(() => toast.error("Couldn't load the page list"))
        .finally(() => setLoadingPages(false));
    }
  };

  const previewFromList = (p) => {
    setPath(p);
    check(p);
    document.querySelector('[data-testid="share-preview-card"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const addOverride = () => {    const p = (overridePath || "").trim();
    if (!p.startsWith("/") || !overrideImage) {
      toast.error("Give a page path starting with / and an image");
      return;
    }
    setOverrides({ ...overrides, [p.replace(/\/$/, "")]: overrideImage });
    setOverridePath("");
    setOverrideImage("");
    toast.success("Added — press Save to apply");
  };

  if (!data) return <p className="text-sm text-slate-400">Loading…</p>;

  // The card URL points at the live host, which is right for a crawler and
  // wrong for this preview — show it from wherever the admin is running.
  const localSrc = (url) => (url || "").replace(data.origin, API.replace(/\/api$/, ""));
  const sectionLabel = (key) => (data.sections.find((s) => s.key === key) || {}).label || key;

  return (
    <div data-testid="share-cards-manager">
      <div className="flex items-start gap-3 mb-6">
        <Share2 className="text-orange shrink-0 mt-1" size={22} />
        <div>
          <h2 className="font-display font-extrabold text-navy text-xl">Share cards</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl leading-relaxed">
            What LinkedIn, Facebook and WhatsApp show when a CASS link is posted. Any URL copied from the address bar
            unfurls on its own — nobody has to remember a special link. Changes apply the next time the site is
            published.
          </p>
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        {[["settings", "Settings"], ["report", "Share report"]].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`share-tab-${key}`}
            className={`text-sm font-semibold px-5 py-2.5 rounded-full border transition-colors ${
              tab === key ? "bg-navy text-white border-navy" : "bg-white text-navy border-slate-200 hover:border-orange"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "report" && <ShareReport />}

      {tab === "settings" && (
      <>
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6" data-testid="share-sections">
        {data.sections.map((s) => (
          <div key={s.key} className="py-4 border-b border-slate-100 last:border-0">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0 max-w-xl">
                <p className="font-display font-bold text-navy">
                  {s.label}
                  {s.pages > 0 && <span className="ml-2 text-xs font-semibold text-slate-400">{s.pages} page{s.pages === 1 ? "" : "s"}</span>}
                </p>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{s.note}</p>
                <p className="text-xs text-slate-400 mt-1">{MODE_HINT[modes[s.key]]}</p>
              </div>
              <div className="flex gap-1.5 shrink-0">
                {data.modes.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setModes({ ...modes, [s.key]: m.key })}
                    data-testid={`share-mode-${s.key}-${m.key}`}
                    className={`text-xs font-semibold px-3.5 py-2 rounded-full border transition-colors ${
                      modes[s.key] === m.key
                        ? "bg-navy text-white border-navy"
                        : "bg-white text-navy border-slate-200 hover:border-orange"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={() => toggleSection(s.key)}
              data-testid={`share-pages-toggle-${s.key}`}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-teal hover:text-navy transition-colors"
            >
              {openSection === s.key ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              {openSection === s.key ? "Hide the pages in this section" : "View the pages in this section"}
            </button>

            {openSection === s.key && (
              <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 p-3" data-testid={`share-pages-${s.key}`}>
                {loadingPages && !pages ? (
                  <p className="text-xs text-slate-400 inline-flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Loading the URLs…</p>
                ) : (
                  (() => {
                    const list = (pages?.[s.key] || []);
                    const q = (filters[s.key] || "").trim().toLowerCase();
                    const shown = q ? list.filter((p) => p.toLowerCase().includes(q)) : list;
                    if (!list.length) return <p className="text-xs text-slate-400">No live pages in this section yet.</p>;
                    return (
                      <>
                        {list.length > 12 && (
                          <div className="relative mb-3">
                            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                              value={filters[s.key] || ""}
                              onChange={(e) => setFilters({ ...filters, [s.key]: e.target.value })}
                              placeholder={`Search ${list.length} URLs`}
                              data-testid={`share-pages-filter-${s.key}`}
                              className="w-full max-w-sm border border-slate-200 rounded-full bg-white pl-8 pr-4 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-orange/40 focus:border-orange"
                            />
                          </div>
                        )}
                        <ul className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                          {shown.map((p) => (
                            <li key={p} className="flex items-center gap-2 py-1.5">
                              <span className="text-xs text-navy font-mono truncate flex-1" title={p}>{p}</span>
                              <button
                                type="button"
                                onClick={() => previewFromList(p)}
                                data-testid={`share-pages-preview-${p}`}
                                className="text-[11px] font-semibold text-teal hover:text-navy shrink-0"
                              >
                                Preview
                              </button>
                              <a
                                href={`${data.origin}${p}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-slate-400 hover:text-navy shrink-0"
                                aria-label={`Open ${p} in a new tab`}
                              >
                                <ExternalLink size={13} />
                              </a>
                            </li>
                          ))}
                        </ul>
                        {!shown.length && <p className="text-xs text-slate-400">Nothing matches that.</p>}
                      </>
                    );
                  })()
                )}
              </div>
            )}
          </div>
        ))}

        <button
          onClick={save}
          disabled={saving}
          data-testid="share-cards-save"
          className="mt-5 inline-flex items-center gap-2 bg-orange hover:bg-orange-dark disabled:opacity-60 text-white font-bold text-sm px-5 py-2.5 rounded-full transition-colors"
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6" data-testid="share-preview-card">
        <h3 className="font-display font-bold text-navy">Preview a page</h3>
        <p className="text-xs text-slate-500 mt-1">Shown at the size LinkedIn renders it, so what you approve is what gets posted.</p>

        <div className="flex flex-wrap gap-2 mt-4">
          <input
            value={path}
            onChange={(e) => setPath(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && check(path)}
            placeholder="/ask-cassie/what-is-the-current-vat-registration-threshold"
            data-testid="share-preview-path"
            className={`${inputCls} max-w-xl`}
          />
          <button
            onClick={() => check(path)}
            data-testid="share-preview-check"
            className="inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-white font-bold text-sm px-5 py-2.5 rounded-full transition-colors"
          >
            {loadingPreview ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Preview
          </button>
          <a
            href={`https://www.linkedin.com/post-inspector/inspect/${encodeURIComponent(`${data.origin}${path}`)}`}
            target="_blank"
            rel="noreferrer"
            data-testid="share-post-inspector"
            className="inline-flex items-center gap-2 border border-slate-200 hover:border-orange text-navy font-bold text-sm px-5 py-2.5 rounded-full transition-colors"
          >
            <ExternalLink size={15} /> LinkedIn Post Inspector
          </a>
        </div>
        <p className="text-xs text-slate-400 mt-2 max-w-2xl leading-relaxed">
          LinkedIn keeps a copy of a preview for about a week. After changing a page's title or image, run the URL
          through the Post Inspector — it's the only way to make LinkedIn fetch the new version.
        </p>

        {preview && (
          <div className="mt-5" data-testid="share-preview-result">
            {preview.resolved === false ? (
              <p className="text-sm text-amber-600">
                That path isn't one we recognise, so it unfurls with the site default.
              </p>
            ) : (
              <>
                <div className="max-w-[600px] rounded-xl overflow-hidden border border-slate-200">
                  {preview.image ? (
                    <img src={localSrc(preview.image)} alt="" className="w-full block bg-slate-100" data-testid="share-preview-image" />
                  ) : (
                    <div className="aspect-[1200/627] flex items-center justify-center bg-slate-100 text-slate-400 text-sm">
                      <ImageIcon size={16} className="mr-2" /> No card — site default
                    </div>
                  )}
                  <div className="p-3 bg-slate-50">
                    <p className="text-[11px] uppercase tracking-wide text-slate-400">cassonline.co.uk</p>
                    <p className="font-display font-bold text-navy text-sm mt-0.5" data-testid="share-preview-title">{preview.title}</p>
                    {preview.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{preview.description}</p>}
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  Section: <span className="font-semibold text-navy">{sectionLabel(preview.section)}</span> · showing the{" "}
                  <span className="font-semibold text-navy">{preview.source === "own" ? "page's own image" : preview.source === "override" ? "uploaded override" : preview.source === "off" ? "site default" : "generated card"}</span>
                </p>
              </>
            )}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6" data-testid="share-overrides">
        <h3 className="font-display font-bold text-navy">Replace the card for one page</h3>
        <p className="text-xs text-slate-500 mt-1 max-w-2xl leading-relaxed">
          For a page that shouldn't carry Cassie's face, or one that deserves its own artwork. An override beats the
          section setting.
        </p>

        {Object.keys(overrides).length > 0 && (
          <ul className="mt-4 space-y-2">
            {Object.entries(overrides).map(([p, img]) => (
              <li key={p} className="flex items-center gap-3 rounded-xl bg-slate-50 p-3">
                <img src={localSrc(img)} alt="" className="w-20 h-11 object-cover rounded-lg bg-white shrink-0" />
                <span className="text-sm text-navy font-semibold truncate flex-1">{p}</span>
                <button
                  type="button"
                  onClick={() => {
                    const next = { ...overrides };
                    delete next[p];
                    setOverrides(next);
                  }}
                  data-testid={`share-override-remove-${p}`}
                  className="text-slate-400 hover:text-red-500 transition-colors"
                  aria-label={`Remove the override for ${p}`}
                >
                  <Trash2 size={15} />
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4 grid gap-3 max-w-xl">
          <input
            value={overridePath}
            onChange={(e) => setOverridePath(e.target.value)}
            placeholder="/ask-cassie/some-answer-slug"
            data-testid="share-override-path"
            className={inputCls}
          />
          <ImageUpload label="Replacement card (1200×627 works best)" value={overrideImage} onChange={setOverrideImage} />
          <button
            type="button"
            onClick={addOverride}
            data-testid="share-override-add"
            className="justify-self-start inline-flex items-center gap-2 border border-slate-200 hover:border-orange text-navy font-bold text-sm px-5 py-2.5 rounded-full transition-colors"
          >
            Add override
          </button>
        </div>
      </div>
      </>
      )}
    </div>
  );
}
